import { useMemo, useCallback } from "react";
import { useNotify, useGetList } from "react-admin";
import { OptimizerCountryEnum, OptimizerFinlandGroupCertificationTypeEnum } from "shared/enums/opti_enums";
import { createGains } from "../gainHelpers";
import { OptimizerResourcesEnum } from "shared/enums/resourceMappings";
import { startOfDay } from "date-fns";
import { SiteContryEnum } from "shared/enums/globalEnums";
import { GroupCertificationsCreatePayload, GroupCertificationsCreateValues } from "../types";
import { isFrance } from "../utils";

export const useGroupCertificationCreate = () => {
  const notify = useNotify();

  const CERTIFICATION_POWER_MAP: Record<OptimizerFinlandGroupCertificationTypeEnum, number> = useMemo(() => {
    return {
      [OptimizerFinlandGroupCertificationTypeEnum.FCR_N]: 0.927,
      [OptimizerFinlandGroupCertificationTypeEnum.FCR_D_SYMMETRICAL]: 1.04,
      [OptimizerFinlandGroupCertificationTypeEnum.AFRR_ASYMMERICAL_FINLAND]: 2.5,
    };
  }, []);

  const { data: certifications, isLoading } = useGetList(OptimizerResourcesEnum.OptiCertifications, {
    filter: { country: OptimizerCountryEnum.FI },
  });

  const certificationIdByName = useMemo(() => {
    return certifications?.reduce<Record<string, number>>((acc, certification) => {
      acc[certification.certif_name] = certification.id;
      return acc;
    }, {} as Record<string, number>);
  }, [certifications]);

  const certificationNameById = useMemo(() => {
    return Object.values(OptimizerFinlandGroupCertificationTypeEnum).reduce<
      Record<number, OptimizerFinlandGroupCertificationTypeEnum>
    >((acc, certifName) => {
      const certifNameStr = certifName.toString();
      if (
        certificationIdByName &&
        certifNameStr in certificationIdByName &&
        certificationIdByName[certifNameStr] !== undefined
      ) {
        acc[certificationIdByName[certifNameStr]] = certifName;
        return acc;
      }
      return acc;
    }, {} as Record<number, OptimizerFinlandGroupCertificationTypeEnum>);
  }, [certificationIdByName]);

  const getCertificationPowerFromId = useCallback(
    (certificationId: number | undefined) => {
      if (!certificationId) {
        return;
      }
      const certifName = certificationNameById[certificationId];
      if (certifName && certifName in CERTIFICATION_POWER_MAP) {
        return CERTIFICATION_POWER_MAP[certifName];
      }
    },
    [certificationNameById, CERTIFICATION_POWER_MAP],
  );

  const transformFormValues = (
    values: GroupCertificationsCreateValues,
  ): GroupCertificationsCreatePayload | Record<string, never> => {
    if (isFrance(values.country as SiteContryEnum)) {
      return {};
    }
    const certifName = certificationNameById[values.certification_id as number];
    return {
      name: `SITE ${values.site_id} ${certifName}`,
      group_type: "fingrid_site",
      group_code: values.site_id,
      certification_id: values.certification_id,
      status: values.status,
      start_date: startOfDay(values.start_date).toISOString(),
    };
  };

  const handleSuccess = useCallback(
    async (data: any, certifiedPower: number, market: OptimizerFinlandGroupCertificationTypeEnum) => {
      try {
        await createGains(data.id, certifiedPower, market, data.group_code);
        notify("Group certification and gains created successfully", { type: "success" });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        notify(`Group certification created but gains creation failed: ${errorMessage}`, { type: "warning" });
        console.error("Error creating gains:", error);
      }
    },
    [notify],
  );

  const handleError = useCallback(
    (error: any) => {
      notify(`Group certification creation failed: ${error.body.detail}`, { type: "error" });
      console.error("Error creating group certification:", error.body.detail);
    },
    [notify],
  );

  return {
    isLoading,
    certificationIdByName,
    certificationNameById,
    getCertificationPowerFromId,
    transformFormValues,
    handleSuccess,
    handleError,
  };
};
